import { Disclosure } from "@headlessui/react";
import {
  type GetRevisionsResponse,
  type GetTicketResponse,
  type GetTicketCommentsResponse,
} from "@mg/schemas/src/christo/catalyst";
import {
  EnterpriseProfileType,
  TicketCommentBoard,
  TicketCommentDisposition,
} from "@mg/schemas/src/commons";
import { type ShowcaseToken } from "@mg/schemas/src/prince/auth";
import {
  CaretDown,
  CaretLeft,
  MagicWand,
  CheckCircle,
  Circle,
  Spinner,
} from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Popover,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  createShapeId,
  type Editor,
  type TLShapeId,
  type TLShape,
  type UnknownRecord,
  type TLImageShape,
  type TLGroupShape,
} from "@tldraw/tldraw";
import cx from "classnames";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Comment } from "./Comment";
import { ResolveWithAgentsModal } from "./ResolveWithAgentsModal";
import { StickyHeader } from "./StickyHeader";

import { AuthTooltip } from "../../../../../components/AuthTooltip";
import { AvatarWithInitials } from "../../../../../components/AvatarWithInitials";
import { CommentLoginWall } from "../../../../../components/CommentLoginWall";
import { SidebarGroup } from "../../../../../components/sidebar/SidebarGroup";
import { SidebarMenu } from "../../../../../components/sidebar/SidebarMenu";
import { SidebarMenuButton } from "../../../../../components/sidebar/SidebarMenuButton";
import { SidebarMenuItem } from "../../../../../components/sidebar/SidebarMenuItem";
import { useSidebar } from "../../../../../components/sidebar/useSidebar";
import { BASEPATH } from "../../../../../config/env";
import { useFlagVariant } from "../../../../../config/featureFlags";
import { useUI } from "../../../../../contexts/ui";
import leapfrogLoader from "../../../../../images/leapfrog-gray.gif";
import { getGuidelines } from "../../../../../services/brand-guidelines";
import {
  requestAIComment,
  ReviewAlreadyInProgressError,
  checkReviewStatus,
} from "../../../../../services/projects";
import {
  errorAnalyticsPayload,
  useAnalytics,
} from "../../../../../utils/analytics";
import {
  canChooseCommentPersonas,
  isDemoEnterprise,
  isPunttGuest,
  personaName,
} from "../../../../../utils/auth";
import { getNameInitials } from "../../../../../utils/constants";
import { isNil } from "../../../../../utils/fp";
import {
  mapRuleToPersona,
  renderReviewCommentAvatar,
} from "../../../../../utils/helpers/ai-persona-avatars";
import {
  useAppDispatch,
  useAppSelector,
  waitForState,
} from "../../../../../utils/hooks";
import { errorMessage } from "../../../../../utils/http";
import { assetForUser } from "../../../../../utils/imageHandler";
import { useReviewRevisionMutation } from "../../../../../utils/queries/projects";
import {
  setActiveCommentId,
  setHighlightedCommentId,
  setSelectedVersionIndex,
} from "../../../../../utils/slices/ticket";
import { safeStorageGet, safeStorageSet } from "../../../../../utils/storage";
import { store } from "../../../../../utils/store";
import { useGetVisibleUsers } from "../../../../../utils/tldraw/comments";
import { renderPersonaDescription } from "../../../../index/view";
import { ticketCommentPathString } from "../../../ticketCommentPaths";
import { useTicket } from "../../ticket/view";

const collapsibleTriggerClasses =
  "z-[10] top-0 sticky h-14 px-4 border-b border-b-puntt-neutral-gray-6 bg-base-white w-full flex items-center justify-between";
const sectionClasses = "max-h-full overflow-y-auto";

type CommentsProps = {
  editor?: Editor | null;
  videoRef: React.RefObject<{
    handleCommentClicked(seekTime: number, commentId?: string): void;
    duration: number;
  }>;
};

function analyticsPayload(
  renderTime: number,
  ticket: GetTicketResponse,
  comments: GetTicketCommentsResponse,
  currentRevision: GetRevisionsResponse[number],
  user: ShowcaseToken | null,
  selectedPersonas: string[],
) {
  const newComments = comments.filter(
    (c) => new Date(c.createdAt).getTime() > renderTime,
  );
  return {
    numComments: comments.length,
    numNewComments: newComments.length,
    numNewAIComments: newComments.filter((c) => c.isAI).length,
    numActiveCommentsOnCurrentRevision: comments.filter(
      (c) =>
        c.boardId === currentRevision?._id &&
        c.disposition === TicketCommentDisposition.DEFAULT,
    ).length,
    numRevisions: ticket.revisionBoards?.length,
    revisionName: currentRevision.name,
    revisionId: currentRevision._id,
    revisionNumber: currentRevision.revision,
    revisionFileTypes: currentRevision.reviewFiles?.map((f) =>
      f.source.slice(-3),
    ),
    minutesSinceRevisionCreated:
      (Date.now() - new Date(currentRevision.createdAt).getTime()) /
      (1000 * 60),
    revisionCreatedByCurrentUser:
      (typeof currentRevision.createdBy === "string"
        ? currentRevision.createdBy
        : currentRevision.createdBy._id) === user?.userID &&
      Boolean(user?.userID),
    isRevisionAIReviewed: currentRevision.isAIReviewed,
    minutesSinceRender: (Date.now() - renderTime) / (1000 * 60),
    numSelectedPersonas: selectedPersonas.length,
    ...selectedPersonas.reduce(
      (acc, persona) => {
        acc[`${persona}PersonaEnabled`] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  };
}

function getStorageKey(enterpriseId: string) {
  return `ai-personas-${enterpriseId}`;
}

/**
 * why: `safeStorage*` can only guard the CALL — the `localStorage` accessor
 * itself throws `SecurityError` where storage is blocked wholesale (sandboxed
 * iframe without `allow-same-origin`, Chrome "block all cookies"), and as an
 * argument it is evaluated before that helper's `try` is ever entered. Resolve
 * the accessor here so the persona toggle degrades to "not persisted" instead
 * of throwing out of its async handler.
 */
function getLocalStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

function getStoredPersonas(enterpriseId: string) {
  const storage = getLocalStorage();
  if (!storage) return {};

  try {
    return JSON.parse(
      safeStorageGet(storage, getStorageKey(enterpriseId)) || "{}",
    ) as Record<"brand" | "legal", boolean>;
  } catch {
    return {};
  }
}

/**
 * This can only appear for design reviews. Creative Briefs
 * cannot get AI reviews on them.
 */
export function Comments(props: CommentsProps) {
  const { t } = useTranslation("ui");
  const { editor } = props;

  const router = useRouter();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const aiPersonas = (query.data?.aiPersonas ?? []).filter((p) => p.enabled);

  const { open } = useSidebar();

  const {
    value: ticket,
    revisions,
    comments,
    activeCommentId,
  } = useAppSelector((state) => state.ticket);
  const user = useAppSelector((state) => state.auth.value);
  // Share-link viewers are stateless guests (JWT carries `referredBy` and a
  // ticket/folder id but no real `role`). Comments for this viewer class are
  // server-side redacted to length-preserving lorem, and we additionally hide
  // the UI behind a blurred + click-locked wall so the lockout is obvious.
  const isGuest = user == null || isPunttGuest();
  const { showResolvedComments, showDismissedComments, showDeletedComments } =
    useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const { notify } = useUI();
  const users = useGetVisibleUsers();

  const aiReviewerAccessKeyRaw = useFlagVariant("run-ai-review");

  // Only allow MEANINGFUL_GIGS, CATALYST_AI, and ADMIN_WITH_AI roles to run AI reviews
  const canRunAIReview =
    user?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
    user?.role === EnterpriseProfileType.CATALYST_AI ||
    user?.role === EnterpriseProfileType.ADMIN_WITH_AI;

  // Demo "Resolve with Agents" superpower — strictly MG only. Distinct from
  // canRunAIReview because ADMIN_WITH_AI is a real customer-tenant role and
  // we don't want paying customers to see the demo modal on their tickets.
  const canResolveWithAgents =
    user?.role === EnterpriseProfileType.MEANINGFUL_GIGS;

  // Roles with explicit AI access should not be gated by the feature flag
  const aiReviewerAccessKey = canRunAIReview
    ? aiReviewerAccessKeyRaw || "always"
    : aiReviewerAccessKeyRaw;

  // we navigate to the revision and pan to the comment
  // shape when clicking a comment within the sidebar
  const activeRevisionIndex = useAppSelector(
    (state) => state.ticket.reversedVersionIndex,
  );

  const enterpriseId = useAppSelector(
    (state) => state.auth.value?.enterpriseId,
  );
  const [personaSettings, setPersonaSettings] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (enterpriseId) {
      setPersonaSettings(getStoredPersonas(enterpriseId));
    }
  }, [enterpriseId]);

  async function setActiveRevisionIndex(index: number) {
    const newIndex = revisions.length - 1 - index;
    dispatch(setSelectedVersionIndex(newIndex));
    await waitForState(
      store,
      (state) => state.ticket.reversedVersionIndex === newIndex,
    );
  }

  const { hasInitiatedAiReview, setHasInitiatedAiReview } = useTicket();
  const reviewRevisionMutation = useReviewRevisionMutation();
  const currentRevision = revisions[activeRevisionIndex!];

  const [aiReviewRunning, setAiReviewRunning] = useState(false);
  const [aiRunningCurrentStep, setAiRunningCurrentStep] = useState(0);
  const [aiRunningProgressBar, setAiRunningProgressBar] = useState(0);
  const [finalizingAIReview, setFinalizingAIReview] = useState(false);
  const [aiReviewCompleted, setAiReviewCompleted] = useState<
    Record<string, boolean>
  >({});
  const [isReviewInProgressOnBackend, setIsReviewInProgressOnBackend] =
    useState(false);
  const [resolveWithAgentsOpen, setResolveWithAgentsOpen] = useState(false);

  const showStartAIReview =
    canRunAIReview &&
    !isDemoEnterprise() &&
    !comments.some((c) => c.isAI) &&
    revisions.length > 0 &&
    !aiReviewCompleted[currentRevision?._id || ""];

  const posthog = useAnalytics("Comments");
  const renderTimeRef = useRef(0);
  const suggestedImprovementsRef = useRef<HTMLButtonElement>(null);
  const requiredImprovementsRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    renderTimeRef.current = Date.now();
  }, [ticket?._id]);

  // Check if a review is already in progress on the backend
  useEffect(() => {
    const checkStatus = async () => {
      if (ticket?._id && currentRevision?._id) {
        try {
          const result = await checkReviewStatus({
            ticket_id: ticket._id,
            board_id: currentRevision._id,
          });
          setIsReviewInProgressOnBackend(result.is_review_in_progress);
        } catch (error) {
          console.error("Failed to check review status:", error);
          // Don't block the UI if the check fails
          setIsReviewInProgressOnBackend(false);
        }
      }
    };

    checkStatus();
  }, [ticket?._id, currentRevision?._id]);

  const pendingCoordsRef = useRef<{
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    const pendingCoords = pendingCoordsRef.current;

    if (pendingCoords != null) {
      editor?.centerOnPoint(pendingCoords);
      pendingCoordsRef.current = null; // reset the pendingCoords in ref
      const shape = editor?.getShapeAtPoint(pendingCoords);

      if (shape) {
        editor?.setSelectedShapes([shape.id]);
        editor?.select(shape);
      }
    }
  }, [editor, activeRevisionIndex]);

  // Support sorting Design Review comments by physical location
  const [sortedComments, setSortedComments] = useState(comments.slice());
  useEffect(() => {
    // Get comments we may show in the sidebar
    const revisionCommentsByBoardId: Record<string, GetTicketCommentsResponse> =
      comments.reduce((map, comment) => {
        if (
          comment.boardType === TicketCommentBoard.REVISION &&
          comment.boardId
        ) {
          if (!map[comment.boardId]) map[comment.boardId] = [];
          map[comment.boardId].push(comment);
        }
        return map;
      }, Object.create(null));

    // Get shapes that can be commented upon
    const imageShapesByBoardId: Record<string, TLImageShape[]> =
      Object.create(null);
    let groupShapesById: Record<string, TLGroupShape> = Object.create(null);
    for (const revision of revisions) {
      const shapes: (UnknownRecord & Partial<TLShape>)[] = Object.values(
        revision.shapes?.store ?? {},
      );
      imageShapesByBoardId[revision._id] = shapes.filter(
        (shape): shape is TLImageShape => {
          return shape.typeName === "shape" && shape.type === "image";
        },
      );
      groupShapesById = shapes.reduce((map, shape) => {
        if (shape.typeName === "shape" && shape.type === "group") {
          map[shape.id] = shape as TLGroupShape;
        }
        return map;
      }, groupShapesById);
    }

    // Shape X/Y coords are relative to their parent, so we have to add the parent coords.
    // We should only ever have one level of parents.
    function getShapeCoord(shape: TLShape, coord: "x" | "y"): number {
      const parent =
        shape.parentId && groupShapesById[shape.parentId]
          ? groupShapesById[shape.parentId][coord]
          : 0;
      return parent + shape[coord];
    }

    // Filter to commented shapes
    const commentedShapesByBoardId: Record<string, TLImageShape[]> =
      Object.create(null);
    const commentsToShapes = new Map<
      GetTicketCommentsResponse[number],
      TLImageShape[]
    >();
    for (const [boardId, imageShapes] of Object.entries(imageShapesByBoardId)) {
      const revisionComments = revisionCommentsByBoardId[boardId];
      if (!revisionComments) continue;

      if (!commentedShapesByBoardId[boardId]) {
        commentedShapesByBoardId[boardId] = [];
      }

      for (const comment of revisionComments) {
        if (comment.x === undefined || comment.y === undefined) continue;
        for (const imageShape of imageShapes) {
          // Check if the comment is inside an imageShape.
          // This could be done more efficiently by e.g. using a quadtree or
          // by calculating which shape comments should be attached to when
          // they're saved or moved. However, on a 2021 M1 Pro, the whole
          // sorting operation with 10 comments and 69 shapes took about 1ms,
          // so it's not worth the effort to make it faster.
          if (
            comment.x >= getShapeCoord(imageShape, "x") &&
            comment.x <= getShapeCoord(imageShape, "x") + imageShape.props.w &&
            comment.y >= getShapeCoord(imageShape, "y") &&
            comment.y <= getShapeCoord(imageShape, "y") + imageShape.props.h
          ) {
            commentedShapesByBoardId[boardId].push(imageShape);

            const commentShapes = commentsToShapes.get(comment);
            if (!commentShapes) {
              commentsToShapes.set(comment, [imageShape]);
            } else {
              commentShapes.push(imageShape);
            }
          }
        }
      }
    }

    // Sort commented shapes top to bottom, then left to right
    const shapeToIndex = new Map<TLImageShape, number>();
    for (const commentedShapes of Object.values(commentedShapesByBoardId)) {
      commentedShapes.sort((a, b) => {
        return (
          getShapeCoord(a, "y") - getShapeCoord(b, "y") ||
          getShapeCoord(a, "x") - getShapeCoord(b, "x")
        );
      });
      for (let i = 0; i < commentedShapes.length; i++) {
        shapeToIndex.set(commentedShapes[i], i);
      }
    }

    // Associate each comment with the shape whose index we can use for sorting
    // Array.from() is due to TypeScript error:
    // Map can only be iterated through when using the '--downlevelIteration' flag or with a '--target' of 'es2015' or higher. ts(2802)
    const commentToShape = new Map<
      GetTicketCommentsResponse[number],
      TLImageShape
    >();
    for (const [comment, shapes] of Array.from(commentsToShapes)) {
      const commentShape = shapes.reduce(
        (smallest, shape) => {
          if (!smallest) return shape;
          return smallest.props.w * smallest.props.h <
            shape.props.w * shape.props.h
            ? smallest
            : shape;
        },
        null as TLImageShape | null,
      ) as TLImageShape;
      commentToShape.set(comment, commentShape);
    }

    // Sort comments by timestamp, then by shape index, then by coordinates
    const newSortedComments = comments.toSorted((a, b) => {
      const aShape = commentToShape.get(a) as TLImageShape;
      const bShape = commentToShape.get(b) as TLImageShape;
      const aShapeIndex = shapeToIndex.get(aShape) ?? comments.length;
      const bShapeIndex = shapeToIndex.get(bShape) ?? comments.length;
      const xDiff = a.x !== undefined && b.x !== undefined ? a.x - b.x : 0;
      const yDiff = a.y !== undefined && b.y !== undefined ? a.y - b.y : 0;

      // If the comments are on the same shape, then if the y-coordinates are
      // close, scan left to right, otherwise top to bottom.
      // aShape === bShape when this is used. 3% is somewhat arbitrary.
      const maxComparableHeight = aShape ? aShape.props.h * 0.03 : 0;

      return (
        (a.videoStart || 0) - (b.videoStart || 0) ||
        aShapeIndex - bShapeIndex ||
        (Math.abs(yDiff) <= maxComparableHeight ? xDiff || yDiff : yDiff) ||
        (a.x === undefined && a.y === undefined ? 1 : 0) -
          (b.x === undefined && b.y === undefined ? 1 : 0) ||
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });

    setSortedComments(newSortedComments);
  }, [revisions, comments, editor]);

  useEffect(() => {
    if (hasInitiatedAiReview && currentRevision?.isAIReady) {
      runAIReview();
    }
  }, [currentRevision?.isAIReady, hasInitiatedAiReview]);

  const randomAiStepDurations = useRef({
    queue: Math.random() * 6000,
    assets: Math.random() * 3000,
    guidelines: Math.random() * 3000,
    comments: Math.random() * 5000,
    review: Math.random() * 2000,
    finalize: Math.random() * 2000,
  });

  const aiRunningSteps = useMemo(
    () => [
      {
        label: "Queued",
        active: hasInitiatedAiReview && !aiReviewRunning,
        duration: 9000 + randomAiStepDurations.current.queue,
        maxProgress: 90,
      },
      {
        label: "Retrieving assets",
        active: aiReviewRunning && !finalizingAIReview,
        duration: 3000 + randomAiStepDurations.current.assets,
      },
      {
        label: "Processing guidelines",
        active: aiReviewRunning && !finalizingAIReview,
        duration: 3000 + randomAiStepDurations.current.guidelines,
      },
      {
        label: "Generating comments",
        active: aiReviewRunning && !finalizingAIReview,
        duration:
          Math.max(
            45_000,
            Math.min(
              20_000,
              10_000 +
                (props.videoRef?.current?.duration
                  ? props.videoRef?.current?.duration / 3
                  : (currentRevision?.reviewFiles?.length ?? 1)) *
                  1000,
            ),
          ) + randomAiStepDurations.current.comments,
      },
      {
        label: "Reviewing comments",
        active: aiReviewRunning && !finalizingAIReview,
        duration: 8000 + randomAiStepDurations.current.review,
        maxProgress: 90,
      },
      {
        label: "Finalizing",
        active: finalizingAIReview,
        duration: 8000 + randomAiStepDurations.current.finalize,
        maxProgress: 90,
      },
    ],
    [
      hasInitiatedAiReview,
      aiReviewRunning,
      finalizingAIReview,
      props.videoRef?.current?.duration,
      currentRevision?.reviewFiles?.length,
    ],
  );

  useEffect(() => {
    if (
      !hasInitiatedAiReview &&
      !aiReviewRunning &&
      aiRunningCurrentStep === 0
    ) {
      return;
    }
    if (!aiRunningSteps[aiRunningCurrentStep]?.active) {
      let interval: number;
      if (aiRunningCurrentStep < aiRunningSteps.length - 1) {
        interval = window.setTimeout(() => {
          setAiRunningCurrentStep(aiRunningCurrentStep + 1);
        }, 500);
      }
      if (aiRunningCurrentStep === aiRunningSteps.length - 1) {
        setAiRunningProgressBar(0);
      }
      return () => clearInterval(interval);
    }

    const step = aiRunningSteps[aiRunningCurrentStep];
    const startTime = performance.now();

    let interval = window.setInterval(() => {
      const elapsed = performance.now() - startTime;
      const newProgress = (elapsed / step.duration) * 100;

      if (newProgress <= (step.maxProgress || 100)) {
        setAiRunningProgressBar(newProgress);
      }

      if (
        elapsed >= step.duration &&
        aiRunningCurrentStep < aiRunningSteps.length - 1 &&
        !step.maxProgress
      ) {
        clearInterval(interval);
        interval = window.setTimeout(() => {
          setAiRunningCurrentStep(aiRunningCurrentStep + 1);
          setAiRunningProgressBar(0);
        }, 500); // Short delay before starting the next step
      }
    }, 100);

    return () => clearInterval(interval);
  }, [
    aiRunningCurrentStep,
    aiRunningSteps,
    hasInitiatedAiReview,
    aiReviewRunning,
  ]);

  function calculateVideoProcessingTime(
    duration: number,
    width?: number,
    height?: number,
  ): number {
    // Base time is 1 minute per minute of video
    let baseTime = Math.ceil(duration / 60);

    // If we have resolution info, adjust the time based on resolution
    if (width && height) {
      // Calculate total pixels (in millions)
      const totalPixels = (width * height) / 1_000_000;

      // Resolution factor:
      // - 1MP (e.g. 1024x1024) = 1x multiplier
      // - 4MP (e.g. 2048x2048) = 1.5x multiplier
      // - 9MP (e.g. 3072x3072) = 2x multiplier
      // - 16MP (e.g. 4096x4096) = 2.5x multiplier
      const resolutionFactor = 1 + (Math.sqrt(totalPixels) * 5 - 1);

      // Apply resolution factor to base time
      baseTime = Math.ceil(baseTime * resolutionFactor);
    }

    return baseTime;
  }

  function renderVideoProcessingMessage() {
    const isVideo = props.videoRef?.current?.duration != null;
    const videoDuration = props.videoRef.current?.duration;

    // Get video dimensions from the video element in the DOM
    const videoElement = document.querySelector(
      "video",
    ) as HTMLVideoElement | null;
    // Only get dimensions if the video is ready (readyState >= 2 means enough data is loaded)
    const isVideoReady =
      videoElement?.readyState != null && videoElement.readyState >= 2;
    const videoWidth = isVideoReady ? videoElement?.videoWidth : undefined;
    const videoHeight = isVideoReady ? videoElement?.videoHeight : undefined;

    const processingMinutes =
      isVideo && videoDuration
        ? calculateVideoProcessingTime(videoDuration, videoWidth, videoHeight)
        : 0;

    if (!isVideo || processingMinutes <= 0 || currentRevision?.isAIReady) {
      return null;
    }

    return (
      <Text size="2" color="gray" className="mb-2">
        Video reviews can take up to {processingMinutes} minutes to process and
        complete. Feel free to leave this page and continue other work.
      </Text>
    );
  }

  function renderAIReviewRunning() {
    if (!hasInitiatedAiReview && !aiReviewRunning) {
      return null;
    }

    return (
      <Flex direction="column" gap="2" className="w-full">
        {aiRunningSteps.map((step, index) => {
          // Determine the status of each step
          const isCompleted = index < aiRunningCurrentStep;
          const isCurrent = index === aiRunningCurrentStep;
          const isPending = index > aiRunningCurrentStep;

          return (
            <Flex
              direction="row"
              justify="between"
              align="center"
              gap="3"
              key={step.label}
              data-testid={`ai-review-step-${index}`}
              className={cx("w-full transition-opacity duration-100", {
                "opacity-30": isPending,
                "opacity-100": !isPending,
              })}
            >
              <Flex align="center" gap="3" className="min-w-0 flex-1">
                {/* Status indicator */}
                <div className="flex size-5 shrink-0 items-center justify-center">
                  {isCompleted ? (
                    <CheckCircle
                      className="text-gray-700"
                      size={20}
                      weight="fill"
                      data-testid={`ai-review-step-${index}-checkmark`}
                    />
                  ) : isCurrent ? (
                    <Spinner
                      className="text-gray-700 animate-spin"
                      size={20}
                      data-testid={`ai-review-step-${index}-spinner`}
                    />
                  ) : (
                    <Circle
                      className="text-gray-300"
                      size={20}
                      data-testid={`ai-review-step-${index}-pending`}
                    />
                  )}
                </div>

                <Text
                  size="2"
                  className={cx("min-w-0 truncate", {
                    "opacity-80": isCompleted,
                    "font-medium": isCurrent,
                  })}
                >
                  {step.label}
                </Text>
              </Flex>

              {/* Progress bar */}
              <div className="h-2 w-16 shrink-0 overflow-hidden rounded bg-puntt-neutral-gray-6">
                <div
                  className="h-full bg-puntt-accent-9 transition-all duration-100"
                  style={{
                    width: `${isCompleted ? 100 : isCurrent ? aiRunningProgressBar : 0}%`,
                  }}
                />
              </div>
            </Flex>
          );
        })}
      </Flex>
    );
  }

  // Open the Required/Suggested drawer when the first comment of that type gets added
  useEffect(() => {
    if (!revisions[activeRevisionIndex!]) return;

    const currentRevisionId = revisions[activeRevisionIndex!]._id;
    const currentRevisionComments = comments.filter(
      (c) => c.boardId === currentRevisionId,
    );
    const hasRequiredChanges = currentRevisionComments.some(
      (comment) => comment.isRequired === true,
    );
    const hasSuggestedImprovements = currentRevisionComments.some(
      (comment) => comment.isRequired === false,
    );

    const isDisclosureOpen = (el: React.RefObject<HTMLButtonElement>) =>
      el.current?.getAttribute("data-headlessui-state") === "open";

    if (hasRequiredChanges && !isDisclosureOpen(requiredImprovementsRef)) {
      requiredImprovementsRef.current?.click();
    }
    if (
      hasSuggestedImprovements &&
      !isDisclosureOpen(suggestedImprovementsRef)
    ) {
      suggestedImprovementsRef.current?.click();
    }
  }, [
    revisions[activeRevisionIndex!],
    comments,
    requiredImprovementsRef,
    suggestedImprovementsRef,
  ]);

  const handleCommentReplySuccess = useCallback(
    (comment: (typeof comments)[number], context: string) => {
      return (data: typeof comment, eventName: string) => {
        updateShapeWithResponse(comment._id, data);
        posthog.capture("changed_comment", {
          isAI: data.isAI,
          comment: data,
          eventName,
          context,
        });
      };
    },
    [],
  );

  const handleCommentReplyError = useCallback(
    (comment: (typeof comments)[number], context: string) => {
      return (
        error: Error | Response,
        data: typeof comment,
        operation: string,
        payload: Record<
          string,
          string | number | boolean | string[] | undefined
        >,
      ) => {
        updateShapeWithResponse(comment._id, data);
        notify({
          title: "Error",
          message: `An error occured while ${operation}: ${errorMessage(error)}`,
          variant: "error",
        });
        posthog.capture("comment_reply_error", {
          ...errorAnalyticsPayload(error),
          operation,
          threadId: comment._id,
          numExistingReplies: comment.messages.length,
          context,
          ...Object.fromEntries(
            Object.entries(payload).map(([key, value]) => [
              "payload_" + (Array.isArray(value) ? "num_" : "") + key,
              Array.isArray(value) ? value.length : value,
            ]),
          ),
        });
      };
    },
    [],
  );

  function getSelectedPersonas() {
    // Checked boxes / defaults from localStorage where the persona is turned on in settings
    const lastUsedPersonas = Object.entries(personaSettings)
      .filter(
        ([key, selected]) =>
          selected &&
          query.data?.aiPersonas?.find((p) => p.name.toLowerCase() === key)
            ?.enabled,
      )
      .map(([key]) => key);
    // API defaults for personas that are not in personaSettings yet
    const apiPersonas = (query.data?.aiPersonas ?? [])
      .filter((p) => {
        return (
          p.enabled &&
          p.default &&
          !Object.hasOwn(personaSettings, p.name.toLowerCase())
        );
      })
      .map((p) => p.name.toLowerCase());

    return lastUsedPersonas.concat(apiPersonas);
  }

  const handlePersonaChange = async (personaId: string, value: boolean) => {
    if (!enterpriseId) return;

    const newSettings = {
      ...personaSettings,
      [personaId]: value,
    };
    setPersonaSettings(newSettings);

    const storage = getLocalStorage();
    if (storage) {
      safeStorageSet(
        storage,
        getStorageKey(enterpriseId),
        JSON.stringify(newSettings),
      );
    }
  };

  // You must be new here :] don't add any hooks below this
  // line as it may give you a React error #302. This
  // component makes use of a lot of conditionals, so be
  // warned.

  if (ticket == null) {
    return null;
  }
  const resolvedComments = sortedComments.filter(
    (c) =>
      c.boardId === currentRevision?._id &&
      c.disposition === TicketCommentDisposition.RESOLVED &&
      c.isAI,
  );
  const dismissedComments = sortedComments.filter(
    (c) =>
      c.boardId === currentRevision?._id &&
      c.disposition === TicketCommentDisposition.DISMISSED &&
      c.isAI,
  );
  let allCommentsFromBoardType = sortedComments.filter((c) => {
    if (
      user?.role !== EnterpriseProfileType.CATALYST_AI &&
      user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS &&
      user?.role !== EnterpriseProfileType.ADMIN_WITH_AI &&
      c.disposition == TicketCommentDisposition.DISMISSED
    ) {
      return false;
    }
    return c.boardType === TicketCommentBoard.REVISION;
  });

  if (!showDismissedComments) {
    allCommentsFromBoardType = allCommentsFromBoardType.filter(
      (c) => c.disposition !== TicketCommentDisposition.DISMISSED,
    );
  }

  if (!showResolvedComments) {
    allCommentsFromBoardType = allCommentsFromBoardType.filter(
      (c) => c.disposition !== TicketCommentDisposition.RESOLVED,
    );
  }

  if (!showDeletedComments) {
    allCommentsFromBoardType = allCommentsFromBoardType.filter(
      (c) => !c.isDeleted,
    );
  }

  const runAIReview = async () => {
    if (currentRevision?.errorDetails?.reason) {
      return notify({
        title: "Your asset was not processed",
        message: `There was an error processing your asset: ${currentRevision.errorDetails.reason}`,
        timeout: 8000,
        variant: "error",
      });
    }
    if (!hasInitiatedAiReview) {
      setAiRunningCurrentStep(0);
      setAiRunningProgressBar(0);
    }
    if (!currentRevision?.isAIReady) {
      return setHasInitiatedAiReview(true);
    }

    setAiReviewRunning(true);
    const shapeId = createShapeId();
    const startTime = performance.now();

    try {
      const startRequestAICommentTime = performance.now();
      posthog.capture(
        "ai_reviewer_start",
        analyticsPayload(
          renderTimeRef.current,
          ticket,
          comments,
          currentRevision,
          user,
          getSelectedPersonas(),
        ),
      );

      let { allComments: newComments } = await requestAIComment({
        ticket_id: ticket?._id as string,
        board_id: currentRevision._id as string,
        personas: getSelectedPersonas(),
      });

      setFinalizingAIReview(true);
      const startRefreshTicketTime = performance.now();
      const startRefreshCommentsTime = performance.now();

      // get most recent comment
      newComments.sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });

      // then filter only the ones with this same latest createdAt date
      // why: an AI review can complete with zero comments, and
      // newComments[0] is undefined for an empty array — reading `.reviewId`
      // off it throws TypeError inside the filter callback.
      const latestReviewId = newComments[0]?.reviewId;
      newComments = newComments.filter((c) => c.reviewId === latestReviewId);

      newComments.forEach((comment) => {
        if (editor == null) {
          return;
        }
        if (comment.x != undefined && comment.y != undefined) {
          const internalShapeId = createShapeId();
          const avatarShapeId = createShapeId();
          // we need to check if the comment is from AI because AI comments are not taking into account the doc mode offset
          editor.createShapes([
            {
              id: avatarShapeId,
              type: "comment-avatar",
              x: comment?.x,
              y: comment?.y,
              props: {
                h: 25,
                w: 25,
              },
              meta: {
                comment: JSON.stringify(comment),
                linkedCommentId: internalShapeId,
              },
            },
            {
              id: internalShapeId,
              type: "comment",
              x: comment?.x,
              y: (comment?.y ?? 0) + 25 / editor.getZoomLevel(),
              meta: {
                comment: JSON.stringify(comment),
                linkedAvatarId: avatarShapeId,
              },
              props: {
                h: 0,
                w: 0,
                commentId: comment._id,
              },
            },
          ]);
        }
      });

      posthog.capture("ai_reviewer", {
        ...analyticsPayload(
          renderTimeRef.current,
          ticket,
          newComments,
          currentRevision,
          user,
          getSelectedPersonas(),
        ),
        totalDurationSeconds: (performance.now() - startTime) / 1000,
        requestAICommentDurationSeconds:
          (startRefreshTicketTime - startRequestAICommentTime) / 1000,
        refreshTicketDurationSeconds:
          (startRefreshCommentsTime - startRefreshTicketTime) / 1000,
        refreshCommentsDurationSeconds:
          (performance.now() - startRefreshCommentsTime) / 1000,
      });
    } catch (err) {
      console.error(err);

      // Check if this is a "review already in progress" error
      if (err instanceof ReviewAlreadyInProgressError) {
        notify({
          title: "Review Already in Progress",
          message:
            "There is already a review in progress on this ticket version. Please check back shortly.",
          timeout: 8000,
          variant: "error",
        });

        // Reset UI state since the review is not actually running locally
        setAiReviewRunning(false);
        setFinalizingAIReview(false);
        setAiRunningCurrentStep(0);
        setAiRunningProgressBar(0);
        if (hasInitiatedAiReview) {
          setHasInitiatedAiReview(false);
        }
        // Keep isReviewInProgressOnBackend as true since it's running on backend
        setIsReviewInProgressOnBackend(true);

        return;
      }

      notify({
        title: "An error occurred while processing the AI Reviewer",
        message: JSON.stringify(err),
        timeout: 8000,
        variant: "error",
      });

      posthog.capture("ai_reviewer_error", {
        ...analyticsPayload(
          renderTimeRef.current,
          ticket,
          comments,
          currentRevision,
          user,
          getSelectedPersonas(),
        ),
        ...errorAnalyticsPayload(err),
        totalDurationSeconds: (performance.now() - startTime) / 1000,
      });
    } finally {
      // Invalidate cached comments so the route loader re-fetches from DB
      queryClient.invalidateQueries({
        queryKey: ["ticket-comments", ticket?._id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tickets", ticket?._id],
      });
      router.invalidate();
    }
    editor?.deleteShapes([shapeId]);
    setAiReviewRunning(false);
    setFinalizingAIReview(false);
    setAiReviewCompleted((prev) => ({
      ...prev,
      [currentRevision?._id || ""]: true,
    }));
    setIsReviewInProgressOnBackend(false);

    if (hasInitiatedAiReview) {
      setHasInitiatedAiReview(false);
    }
  };

  if (allCommentsFromBoardType.length === 0) {
    // Share-link viewers (guests) need an auth surface even on empty tickets.
    // The server redacts comment bodies for guests, so an "empty" comment
    // count from the guest's perspective could just be "everything is
    // hidden" — and without the wall there is no entry point into the
    // email-only gate from the comments panel. Render the lockout
    // unconditionally for guests so the CTA is always reachable.
    if (isGuest) {
      return (
        <section
          className={cx(
            "grid w-auto place-content-center justify-items-center gap-y-4 text-wrap p-4",
            {
              hidden: !open,
            },
          )}
        >
          <CommentLoginWall count={0} section="comments" />
        </section>
      );
    }

    // Narrowed to the same MG-only / zero-AI-comments gate as the inline
    // CTA elsewhere. Non-MG roles fall through to the simpler "No comments
    // yet" text below instead of getting AI-themed copy with no button
    // underneath it.
    if (showStartAIReview) {
      return (
        <section className="grid w-auto place-content-center justify-items-center gap-y-4 text-wrap p-4">
          <Skeleton loading={ticket?.isPending}>
            <Text
              size="3"
              weight="regular"
              align="center"
              className={cx({ hidden: !open })}
            >
              {resolvedComments.length > 0 || dismissedComments.length > 0
                ? t("tickets.comments.allCommentsWereResolvedDismissed")
                : t("tickets.comments.noCommentsYetCanAdd")}
            </Text>
          </Skeleton>
          {renderAIReviewButton()}
        </section>
      );
    }

    return (
      <section
        className={cx(
          "grid w-auto place-content-center justify-items-center gap-y-4 text-wrap p-4",
          {
            hidden: !open,
          },
        )}
      >
        <Text size="3">{t("tickets.comments.noCommentsYetCanAdd2")}</Text>
      </section>
    );
  }

  function focusCommentShape(shapeId: TLShapeId) {
    const shapePoints = editor?.getShape(shapeId);

    editor?.run(() => {
      editor.setSelectedShapes([shapeId]);
      editor.centerOnPoint({
        x: shapePoints?.x as number,
        y: shapePoints?.y as number,
      });
    });
  }

  function getShapeFromComment(commentId: string) {
    const shapes = editor?.getCurrentPageShapes() ?? [];
    const thisShape = shapes.find((shape) => {
      if (shape.type !== "comment") {
        return null;
      }

      try {
        const meta = JSON.parse((shape.meta.comment as string) ?? "{}");

        if (
          typeof meta === "object" &&
          meta !== null &&
          "_id" in meta &&
          meta._id === commentId
        ) {
          return shape;
        }
      } catch {
        return null;
      }

      return null;
    });

    return thisShape;
  }

  async function navigateToCommentShape(boardId: string, commentId: string) {
    const nextRevision = revisions.findIndex((rev) => rev._id === boardId);
    dispatch(setActiveCommentId(commentId));
    // Mirror the cold deep-link route loader which dispatches both ids.
    // CommentPin reads highlightedCommentId for its visual scale state.
    dispatch(setHighlightedCommentId(commentId));
    if (nextRevision !== activeRevisionIndex) {
      setActiveRevisionIndex(nextRevision).then(() => {
        const comment = comments.find((c) => c._id === commentId);
        if (comment) {
          pendingCoordsRef.current = {
            x: comment.x as number,
            y: comment.y as number,
          };
        }
      });
    }
    if (
      props.videoRef.current &&
      comments.find((c) => c._id === commentId)?.videoStart
    ) {
      props.videoRef.current.handleCommentClicked(
        comments.find((c) => c._id === commentId)?.videoStart as number,
        commentId,
      );
    } else {
      editor?.run(() => {
        const shapeId = getShapeFromComment(commentId)?.id;

        if (shapeId == null) {
          return;
        }

        focusCommentShape(shapeId);
      });
    }

    // Ask Puntt parity: put the comment id in the URL so links are shareable.
    // @tanstack/history patches window.history.replaceState at the instance
    // level (win.history.replaceState = function(...) { onPushPop(); ... }).
    // Calling the patched instance notifies TanStack Router, which re-matches
    // the URL, sees ticketCommentRoute, and starts a sibling-route transition
    // from ticketRoute — unmounting TicketView, showing a 500 ms
    // PendingComponent flash, and resetting TLDraw editor state (canvas pin
    // selection lost). Going through the prototype bypasses the patch so
    // TanStack does NOT receive the URL change.
    // Redux already has the active comment; the URL update is shareability only.
    // Cold deep links still load via ticketCommentRoute.
    const ticketId = ticket?._id ? String(ticket._id) : "";
    if (ticketId && boardId && commentId) {
      const commentPath = ticketCommentPathString(ticketId, boardId, commentId);
      const base = BASEPATH === "/" ? "" : BASEPATH.replace(/\/$/, "");
      const targetHref = base + commentPath;
      if (window.location.pathname !== targetHref) {
        Object.getPrototypeOf(window.history).replaceState.call(
          window.history,
          null,
          "",
          targetHref,
        );
      }
    }
  }

  function updateShapeWithResponse(
    commentId: string,
    data: GetTicketCommentsResponse[number],
  ) {
    const comment = comments.find((c) => c._id === commentId);
    if (comment == null) return;

    const isSuggestedOpen =
      suggestedImprovementsRef.current?.getAttribute("aria-expanded");
    const isRequiredOpen =
      requiredImprovementsRef.current?.getAttribute("aria-expanded");

    if (!data.isRequired && comment.isRequired && isSuggestedOpen === "false") {
      suggestedImprovementsRef.current?.click();
    }
    if (data.isRequired && !comment.isRequired && isRequiredOpen === "false") {
      requiredImprovementsRef.current?.click();
    }
    const shape = getShapeFromComment(commentId);
    if (shape == null) return;

    editor?.run(() => {
      editor.setSelectedShapes([shape.id]);
      if (
        user?.role !== EnterpriseProfileType.CATALYST_AI &&
        user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS &&
        user?.role !== EnterpriseProfileType.ADMIN_WITH_AI &&
        (data.disposition == TicketCommentDisposition.DISMISSED ||
          data.disposition == TicketCommentDisposition.RESOLVED)
      ) {
        editor.deleteShapes([shape.id, shape.meta.linkedAvatarId as TLShapeId]);
        return;
      }

      editor.updateShape({
        ...shape,
        meta: {
          ...shape.meta,
          comment: JSON.stringify(data),
        },
        props: {
          ...shape.props,
          commentId: data._id,
        },
      });
      editor.bringToFront([shape.id]);
    });
  }

  const pendingReviewComments = allCommentsFromBoardType.filter(
    (c) =>
      c.isPending &&
      c.createdBy &&
      (typeof c.createdBy === "string" ? c.createdBy : c.createdBy._id) ===
        user?.userID,
  );

  // Demo S7: the seeded Vitalora ticket comments are AI-authored
  // (`createdBy = VITALORA_AI_USER_ID`, `isPending = false`) and
  // already attached to the board so the canvas reads correctly on
  // every fresh deep link. The "Resolve with Agents" demo flow needs
  // those AI comments — not Sarah's drafts — fed into the routing
  // modal. Compute a demo-scoped list of unresolved AI comments with
  // a mention so the modal can group by owner. Outside the demo
  // tenant this stays empty and the existing `pendingReviewComments`
  // gating is unchanged.
  const onDemoEnterprise = isDemoEnterprise();
  const demoRoutableAIComments = onDemoEnterprise
    ? allCommentsFromBoardType.filter(
        (c) =>
          c.isAI &&
          !c.isDeleted &&
          c.disposition !== TicketCommentDisposition.RESOLVED &&
          Array.isArray(c.mentions) &&
          c.mentions.length > 0,
      )
    : [];

  const currentBoardComments = allCommentsFromBoardType.filter(
    (c) => c.boardId === currentRevision?._id,
  );

  function canSubmitReview() {
    // only MG and AI can leave reviews. If we can't get to this case, then
    // cool.
    return true;
  }

  function handleSubmitReview() {
    if (ticket == null || !canSubmitReview()) {
      return;
    }

    reviewRevisionMutation
      .mutateAsync({
        payload: {
          ticketId: ticket._id,
          boardId: revisions[activeRevisionIndex!]?._id,
          creatives: [],
          approval: false,
        },
      })
      .then(() => {
        router.invalidate();
      });
  }

  if (pendingReviewComments.length > 0) {
    const previousComments = currentBoardComments.filter((c) => !c.isPending);

    return (
      <section className={sectionClasses}>
        <div data-testid="pending-comments">
          <StickyHeader
            label={t("tickets.comments.pendingReview")}
            action={
              <Flex gap="2" align="center">
                <Button
                  data-testid="comments-button-1313"
                  size="1"
                  onClick={runAIReview}
                  variant="solid"
                  className={cx({
                    hidden: !canRunAIReview || aiReviewerAccessKey !== "always",
                  })}
                  disabled={aiReviewRunning || isReviewInProgressOnBackend}
                  loading={aiReviewRunning || isReviewInProgressOnBackend}
                >
                  <MagicWand color="rgb(var(--base-white))" />
                  {isReviewInProgressOnBackend
                    ? t("tickets.comments.reviewProgress")
                    : t("tickets.comments.aiReview")}
                </Button>

                {/* Demo S7: split AI comments by owner, fire Slack/Wrike,
                    schedule follow-ups. Strictly MG-only — the modal is a
                    cosmetic demo flow (no real Slack/Wrike API calls) and
                    must never appear on a paying-customer ticket. The
                    seeded Vitalora tickets ship with AI comments already
                    on the board (so the canvas reads correctly without
                    requiring the buyer to click AI Review first), so the
                    demo branch surfaces the button against
                    `demoRoutableAIComments` rather than Sarah's drafts.
                    Real customers keep the original
                    `pendingReviewComments` gate so the button only
                    appears after a fresh AI review run. */}
                {canResolveWithAgents &&
                onDemoEnterprise &&
                demoRoutableAIComments.length > 0 ? (
                  <Button
                    size="1"
                    variant="soft"
                    data-testid="resolve-with-agents-button"
                    onClick={() => setResolveWithAgentsOpen(true)}
                  >
                    {t("tickets.comments.resolveAgents")}
                  </Button>
                ) : null}

                <Button
                  data-testid="comments-button-1329"
                  size="1"
                  onClick={() => handleSubmitReview()}
                  disabled={reviewRevisionMutation.isPending}
                  loading={reviewRevisionMutation.isPending}
                >
                  {t("tickets.comments.finishReview")}
                </Button>
              </Flex>
            }
          />

          <ResolveWithAgentsModal
            open={resolveWithAgentsOpen}
            onClose={() => setResolveWithAgentsOpen(false)}
            comments={(onDemoEnterprise
              ? demoRoutableAIComments
              : pendingReviewComments
            ).map((c) => ({
              _id: c._id as string,
              description: c.description,
              rule: c.rule,
              mentions: c.mentions as string[],
            }))}
            users={(users ?? []).map((u) => ({
              _id: u._id,
              name: u.name,
              avatar: u.avatar,
            }))}
            ticketTitle={ticket?.title || "this asset"}
          />

          <div className={cx({ hidden: pendingReviewComments.length === 0 })}>
            <StickyHeader label={t("tickets.comments.pendingReview")} />

            {isGuest ? (
              <CommentLoginWall
                count={pendingReviewComments.length}
                section="pending-comments"
              />
            ) : (
              pendingReviewComments.map((comment, index) => (
                <Comment
                  data-testid="comments-comment-1351"
                  mentions={comment.mentions as string[]}
                  users={users}
                  editor={editor}
                  rule={comment.rule}
                  key={comment._id}
                  author={
                    comment.createdBy as Exclude<
                      typeof comment.createdBy,
                      string
                    >
                  }
                  isAI={comment.isAI}
                  message={comment.description}
                  messageId={comment._id}
                  meta={comment}
                  replies={comment.messages}
                  title={revisions.find((r) => r._id === comment.boardId)?.name}
                  onClick={() =>
                    navigateToCommentShape(
                      comment.boardId as string,
                      comment._id,
                    )
                  }
                  createdAt={comment.createdAt}
                  updatedAt={comment.updatedAt}
                  onSuccess={handleCommentReplySuccess(comment, "pending")}
                  onError={handleCommentReplyError(comment, "pending")}
                  isActive={comment._id === activeCommentId}
                  videoStart={comment.videoStart}
                  commentId={comment._id}
                  aiPersona={comment.aiPersona}
                  isLast={index === pendingReviewComments.length - 1}
                />
              ))
            )}
          </div>
        </div>

        <div
          data-testid="previous-comments"
          className={cx({ hidden: previousComments.length === 0 })}
        >
          <StickyHeader label={t("tickets.comments.previousComments")} />

          {isGuest ? (
            <CommentLoginWall
              count={previousComments.length}
              section="previous-comments"
            />
          ) : (
            previousComments.map((comment, index) => (
              <Comment
                data-testid="comments-comment-1403"
                mentions={comment.mentions as string[]}
                users={users}
                editor={editor}
                rule={comment.rule}
                key={comment._id}
                author={
                  comment.createdBy as Exclude<typeof comment.createdBy, string>
                }
                isAI={comment.isAI}
                message={comment.description}
                messageId={comment._id}
                meta={comment}
                replies={comment.messages}
                onClick={() =>
                  navigateToCommentShape(comment.boardId as string, comment._id)
                }
                createdAt={comment.createdAt}
                updatedAt={comment.updatedAt}
                onSuccess={handleCommentReplySuccess(
                  comment,
                  "previous pending",
                )}
                onError={handleCommentReplyError(comment, "previous pending")}
                isActive={comment._id === activeCommentId}
                videoStart={comment.videoStart}
                commentId={comment._id}
                aiPersona={comment.aiPersona}
                isLast={index === previousComments.length - 1}
              />
            ))
          )}
        </div>
      </section>
    );
  }

  function isRevisionReadyForAI(revision: typeof currentRevision) {
    if (!revision) return false;

    // If revision is older than 1 hour, allow AI review even if isAIReady is false
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    const isOlderThanOneHour = new Date(revision.createdAt) < oneHourAgo;

    return revision.isAIReady || isOlderThanOneHour;
  }

  function renderAIReviewButton() {
    // Single gate — see `showStartAIReview` definition for the full
    // rationale (MG only, non-demo, zero AI comments, fresh revision).
    // Folds in the historical `!revisions.length`, `aiReviewCompleted`,
    // and `isDemoEnterprise` short-circuits.
    if (!showStartAIReview) return null;
    const isDisabled =
      aiReviewRunning ||
      hasInitiatedAiReview ||
      query.isLoading ||
      !isRevisionReadyForAI(currentRevision) ||
      isReviewInProgressOnBackend;
    const canChoosePersonas =
      canChooseCommentPersonas() &&
      (query.data?.aiPersonas?.filter((p) => p.enabled) ?? []).length > 1;

    if (!open) {
      return (
        <AuthTooltip>
          <Skeleton loading={ticket?.isPending}>
            <SidebarMenuButton
              data-testid="comments-run-ai-review-button"
              asChild
              onClick={runAIReview}
              disabled={isDisabled}
              tooltipClassName={cx("bg-puntt-accent-9", {
                hidden:
                  resolvedComments.length > 0 ||
                  dismissedComments.length > 0 ||
                  aiReviewCompleted[currentRevision?._id || ""] === true,
              })}
              tooltipSide="left"
              tooltip={
                <button
                  data-testid="start-ai-review-tooltip-button"
                  onClick={runAIReview}
                  disabled={isDisabled}
                  className="flex items-center gap-3 bg-transparent"
                >
                  <Text size="3" className="text-base-white">
                    {isReviewInProgressOnBackend
                      ? t("tickets.comments.reviewProgress")
                      : isRevisionReadyForAI(currentRevision)
                        ? t("tickets.comments.startAiReview")
                        : t("tickets.comments.preparingAi")}
                  </Text>
                  {isReviewInProgressOnBackend ? (
                    <img
                      alt={t("tickets.comments.leapFrogLoader")}
                      src={leapfrogLoader}
                      style={{
                        width: "18px",
                        height: "18px",
                        objectFit: "contain",
                      }}
                    />
                  ) : isRevisionReadyForAI(currentRevision) ? (
                    <MagicWand size={18} color="white" />
                  ) : (
                    <img
                      alt={t("tickets.comments.leapFrogLoader")}
                      src={leapfrogLoader}
                      style={{
                        width: "18px",
                        height: "18px",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </button>
              }
            >
              <Button
                variant="solid"
                color="blue"
                className={cx("size-10", {
                  "bg-puntt-neutral-gray-8 p-0": isDisabled,
                  "p-2.5": !isDisabled,
                })}
              >
                {aiReviewRunning ||
                hasInitiatedAiReview ||
                isReviewInProgressOnBackend ? (
                  <img
                    alt={t("tickets.comments.leapFrogLoader")}
                    src={leapfrogLoader}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : isRevisionReadyForAI(currentRevision) ? (
                  <MagicWand size={18} color="white" />
                ) : (
                  <img
                    alt={t("tickets.comments.leapFrogLoader")}
                    src={leapfrogLoader}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Button>
            </SidebarMenuButton>
          </Skeleton>
        </AuthTooltip>
      );
    }

    return (
      <>
        <AuthTooltip>
          <Skeleton loading={ticket?.isPending}>
            <Popover.Root>
              <Flex
                width="100%"
                className={cx({
                  hidden:
                    resolvedComments.length > 0 ||
                    dismissedComments.length > 0 ||
                    aiReviewCompleted[currentRevision?._id || ""] === true,
                })}
              >
                <Button
                  data-testid="start-ai-review-button"
                  size="2"
                  onClick={runAIReview}
                  variant="solid"
                  className={cx(
                    "relative flex-1 cursor-pointer transition-colors disabled:cursor-not-allowed disabled:text-base-white",
                    {
                      "bg-puntt-neutral-gray-8": isDisabled,
                      "bg-puntt-accent-9 hover:bg-puntt-accent-10": !isDisabled,
                      "rounded-r-none":
                        canChoosePersonas &&
                        !(aiReviewRunning || hasInitiatedAiReview),
                      "justify-center":
                        !canChoosePersonas ||
                        aiReviewRunning ||
                        hasInitiatedAiReview,
                    },
                  )}
                  disabled={isDisabled}
                >
                  {aiReviewRunning || hasInitiatedAiReview ? (
                    <span className="flex w-full items-center justify-center gap-2">
                      <img
                        alt={t("tickets.comments.leapFrogLoader")}
                        src={leapfrogLoader}
                        style={{
                          width: "18px",
                          height: "18px",
                          objectFit: "contain",
                        }}
                      />
                    </span>
                  ) : isReviewInProgressOnBackend ? (
                    <span className="flex w-full items-center justify-center gap-2">
                      <img
                        alt={t("tickets.comments.leapFrogLoader")}
                        src={leapfrogLoader}
                        style={{
                          width: "18px",
                          height: "18px",
                          objectFit: "contain",
                        }}
                      />
                      <span>{t("tickets.comments.reviewProgress")}</span>
                    </span>
                  ) : isRevisionReadyForAI(currentRevision) ? (
                    <span className="flex w-full items-center justify-center gap-2">
                      <MagicWand />
                      <span>{t("tickets.comments.startAiReview")}</span>
                    </span>
                  ) : (
                    <span className="flex w-full items-center justify-center gap-2">
                      <img
                        alt={t("tickets.comments.leapFrogLoader")}
                        src={leapfrogLoader}
                        style={{
                          width: "18px",
                          height: "18px",
                          objectFit: "contain",
                        }}
                      />
                      <span>{t("tickets.comments.preparingAi")}</span>
                    </span>
                  )}
                </Button>

                <Popover.Trigger
                  disabled={isDisabled}
                  data-testid="start-ai-review-persona-caret"
                  aria-label="Choose AI review personas"
                  className={cx({
                    "bg-puntt-neutral-gray-8": isDisabled,
                    hidden:
                      !canChoosePersonas ||
                      aiReviewRunning ||
                      hasInitiatedAiReview,
                  })}
                >
                  <IconButton size="2" className="rounded-l-none">
                    <CaretDown />
                  </IconButton>
                </Popover.Trigger>
              </Flex>

              <Popover.Content align="end" width="288px">
                {aiPersonas.map((persona) => (
                  <Flex key={persona.name} gap="2" py="1">
                    <Box py="1">
                      <Checkbox
                        data-testid={`start-ai-review-persona-checkbox-${persona.name.toLowerCase()}`}
                        aria-label={`${personaName(persona.name)} Review`}
                        defaultChecked={getSelectedPersonas().includes(
                          persona.name.toLowerCase(),
                        )}
                        onCheckedChange={(checked) =>
                          handlePersonaChange(
                            persona.name.toLowerCase(),
                            Boolean(checked),
                          )
                        }
                      />
                    </Box>

                    <Box>
                      <Text as="p" size="3" weight="medium">
                        {personaName(persona.name)} Review
                      </Text>
                      <Text>{renderPersonaDescription([persona.name])}</Text>
                    </Box>
                  </Flex>
                ))}
              </Popover.Content>
            </Popover.Root>
          </Skeleton>
        </AuthTooltip>
        {renderAIReviewRunning()}
        {renderVideoProcessingMessage()}
      </>
    );
  }

  function renderPriorityAdjustments() {
    const activeRevisionPriorityAdjustments = currentBoardComments.filter(
      (comment) => comment.isRequired === true,
    );

    return (
      <Disclosure
        defaultOpen={activeRevisionPriorityAdjustments.length > 0}
        as="div"
        className="relative"
        data-testid="required-changes-section"
      >
        {({ open }) => (
          <>
            <Disclosure.Button
              className={collapsibleTriggerClasses}
              ref={requiredImprovementsRef}
            >
              <Flex gap="2" align="center">
                <Text size="3" weight="medium">
                  {t("tickets.comments.requiredChanges")}
                </Text>

                <Badge
                  size="1"
                  variant="solid"
                  color={
                    activeRevisionPriorityAdjustments.length > 0
                      ? "red"
                      : "grass"
                  }
                >
                  {activeRevisionPriorityAdjustments.length}
                </Badge>
              </Flex>

              <CaretLeft
                className={cx("transition-transform", {
                  "-rotate-90": open,
                })}
              />
            </Disclosure.Button>
            <Disclosure.Panel>
              <section className="relative">
                <StickyHeader
                  label={"V" + (revisions.length - activeRevisionIndex!)}
                  className={cx("top-14 border-b-0", {
                    hidden: activeRevisionPriorityAdjustments.length === 0,
                  })}
                />
                {isGuest ? (
                  <CommentLoginWall
                    count={activeRevisionPriorityAdjustments.length}
                    section="required-changes"
                  />
                ) : (
                  activeRevisionPriorityAdjustments.map((comment, index) => (
                    <Comment
                      data-testid="comments-comment-1747"
                      mentions={comment.mentions as string[]}
                      users={users}
                      editor={editor}
                      rule={comment.rule}
                      key={comment._id}
                      author={
                        comment.createdBy as Exclude<
                          typeof comment.createdBy,
                          string
                        >
                      }
                      createdAt={comment.createdAt}
                      isAI={comment.isAI}
                      message={comment.description}
                      messageId={comment._id}
                      meta={comment}
                      replies={comment.messages}
                      onClick={() =>
                        navigateToCommentShape(
                          comment.boardId as string,
                          comment._id,
                        )
                      }
                      updatedAt={comment.updatedAt}
                      onSuccess={handleCommentReplySuccess(comment, "required")}
                      onError={handleCommentReplyError(comment, "required")}
                      isActive={comment._id === activeCommentId}
                      videoStart={comment.videoStart}
                      commentId={comment._id}
                      aiPersona={comment.aiPersona}
                      isLast={
                        index === activeRevisionPriorityAdjustments.length - 1
                      }
                    />
                  ))
                )}
              </section>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    );
  }

  function renderSuggestedImprovements() {
    const activeRevisionSuggestedImprovements = currentBoardComments.filter(
      (comment) => comment.isRequired === false,
    );

    return (
      <Disclosure
        as="div"
        className="relative"
        defaultOpen={activeRevisionSuggestedImprovements.length > 0}
        data-testid="suggested-improvements-section"
      >
        {({ open }) => (
          <>
            <Disclosure.Button
              className={collapsibleTriggerClasses}
              ref={suggestedImprovementsRef}
            >
              <Flex gap="2" align="center">
                <Text size="3" weight="medium">
                  {t("tickets.comments.suggestedImprovements")}
                </Text>

                <Badge size="1" variant="solid" color="gray">
                  {activeRevisionSuggestedImprovements.length}
                </Badge>
              </Flex>

              <CaretLeft
                className={cx("transition-transform", {
                  "-rotate-90": open,
                })}
              />
            </Disclosure.Button>
            <Disclosure.Panel>
              <section className="relative">
                <StickyHeader
                  label={"V" + (revisions.length - activeRevisionIndex!)}
                  className={cx("top-14 border-b-0", {
                    hidden: activeRevisionSuggestedImprovements.length === 0,
                  })}
                />
                {isGuest ? (
                  <CommentLoginWall
                    count={activeRevisionSuggestedImprovements.length}
                    section="suggested-improvements"
                  />
                ) : (
                  activeRevisionSuggestedImprovements.map((comment, index) => (
                    <Comment
                      data-testid="comments-comment-1841"
                      mentions={comment.mentions as string[]}
                      users={users}
                      editor={editor}
                      rule={comment.rule}
                      key={comment._id}
                      author={
                        comment.createdBy as Exclude<
                          typeof comment.createdBy,
                          string
                        >
                      }
                      createdAt={comment.createdAt}
                      isAI={comment.isAI}
                      message={comment.description}
                      messageId={comment._id}
                      meta={comment}
                      replies={comment.messages}
                      onClick={() =>
                        navigateToCommentShape(
                          comment.boardId as string,
                          comment._id,
                        )
                      }
                      updatedAt={comment.updatedAt}
                      onSuccess={handleCommentReplySuccess(
                        comment,
                        "suggested",
                      )}
                      onError={handleCommentReplyError(comment, "suggested")}
                      isActive={comment._id === activeCommentId}
                      videoStart={comment.videoStart}
                      commentId={comment._id}
                      aiPersona={comment.aiPersona}
                      isLast={
                        index === activeRevisionSuggestedImprovements.length - 1
                      }
                    />
                  ))
                )}
              </section>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    );
  }

  function renderCommentsWithCounts() {
    const countedComments = new Map<string, number>();
    const uniqueCommentAuthors = new Map<
      string,
      Exclude<(typeof currentBoardComments)[number]["createdBy"], string> & {
        isAI?: boolean;
      }
    >();
    const activeRevisionRequiredChanges = currentBoardComments.filter(
      (comment) => comment.isRequired === true,
    );
    const activeRevisionSuggestedImprovements = currentBoardComments.filter(
      (comment) => comment.isRequired === false,
    );

    [
      ...activeRevisionRequiredChanges,
      ...activeRevisionSuggestedImprovements,
    ].forEach((comment) => {
      const author = comment.createdBy as Exclude<
        typeof comment.createdBy,
        string
      >;

      if (!isNil(author) && !isNil(author._id)) {
        if (comment.isAI) {
          const persona = (
            comment.aiPersona?.avatar
              ? comment.aiPersona.avatar
              : mapRuleToPersona(comment.rule)
          ).toLowerCase();
          if (!uniqueCommentAuthors.has(persona)) {
            uniqueCommentAuthors.set(persona, {
              ...author,
              isAI: true,
              avatar: persona,
            });
          }

          countedComments.set(persona, (countedComments.get(persona) || 0) + 1);
        } else {
          if (!uniqueCommentAuthors.has(author._id)) {
            uniqueCommentAuthors.set(author._id, author);
          }

          countedComments.set(
            author._id,
            (countedComments.get(author._id) || 0) + 1,
          );
        }
      }
    });

    return Array.from(countedComments.entries()).map(([authorId, count]) => {
      const author = uniqueCommentAuthors.get(authorId);
      if (!author) return null;

      return (
        <SidebarMenuItem key={authorId} className="flex items-center gap-1">
          {author.isAI ? (
            renderReviewCommentAvatar({ isRequired: false })
          ) : (
            <AvatarWithInitials
              size={6}
              avatar={assetForUser(author.avatar)}
              name={getNameInitials(author.name)}
            />
          )}
          <Text size="1" color="gray">
            {count.toLocaleString()}
          </Text>
        </SidebarMenuItem>
      );
    });
  }

  if (!open) {
    return (
      <SidebarGroup>
        <SidebarMenu className="gap-2">
          {showStartAIReview && (
            <SidebarMenuItem
              data-testid="sidebar-menu-items-run-ai-review"
              data-auth-trigger="run-ai-review"
              className="border-b border-b-puntt-neutral-gray-6 px-4 py-2"
            >
              {renderAIReviewButton()}
            </SidebarMenuItem>
          )}

          {renderCommentsWithCounts()}
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  if (revisions.length === 0) {
    return (
      <Box p="8">
        <Text as="p" align="center">
          {t("tickets.comments.createNewVersionAddComments")}
        </Text>
      </Box>
    );
  }

  return (
    <section className={sectionClasses}>
      {/* Conditional render (not `hidden`) so we never leave a padded
          `border-b` shell behind when the CTA is gated off — the previous
          `hidden`-class approach left a 1-pixel-tall empty bar on demo and
          non-MG tickets because `renderAIReviewButton` short-circuited
          internally. `min-h-14` + `flex-col justify-center` matches the
          h-14 disclosure-trigger / SidebarHeader rows above and below it,
          and grows naturally when the in-progress step indicators expand
          beneath the button during a live AI review. */}
      {showStartAIReview && (
        <div className="flex min-h-14 flex-col justify-center border-b border-b-puntt-neutral-gray-6 px-4 py-2">
          {renderAIReviewButton()}
        </div>
      )}

      {/* Demo S7 entry point. The seeded Vitalora ticket comments are
          AI-authored and already on the board (no pending drafts to
          resolve), so the early-return path above — which holds the
          "Resolve with Agents" button next to "AI Review" — never
          fires. Surface the same button here, gated to the demo
          tenant, MG-only, with at least one routable AI comment. The
          modal it opens is purely cosmetic (no real Slack / Wrike /
          Asana writes), so this stays strictly demo-only via
          `onDemoEnterprise` even though `canResolveWithAgents` would
          alone be enough on a paying-customer ticket. */}
      {canResolveWithAgents &&
      onDemoEnterprise &&
      demoRoutableAIComments.length > 0 ? (
        // Snap the bar to the standard h-14 (56px) row height that the
        // SidebarHeader, AI-review CTA, and disclosure triggers use, so
        // the comments sidebar reads as a uniform grid of nav-height
        // rows. The button itself stays compact (`size="1"`); only the
        // surrounding bar grows to match the rest of the chrome.
        <div className="flex h-14 items-center justify-end border-b border-b-puntt-neutral-gray-6 px-4">
          <Button
            size="1"
            variant="soft"
            data-testid="resolve-with-agents-button-demo"
            onClick={() => setResolveWithAgentsOpen(true)}
          >
            {t("tickets.comments.resolveAgents")}
          </Button>
          <ResolveWithAgentsModal
            open={resolveWithAgentsOpen}
            onClose={() => setResolveWithAgentsOpen(false)}
            comments={demoRoutableAIComments.map((c) => ({
              _id: c._id as string,
              description: c.description,
              rule: c.rule,
              mentions: c.mentions as string[] | undefined,
            }))}
            users={(users ?? []).map((u) => ({
              _id: u._id,
              name: u.name,
              avatar: u.avatar,
            }))}
            ticketTitle={ticket?.title || "this asset"}
          />
        </div>
      ) : null}

      {renderPriorityAdjustments()}
      {renderSuggestedImprovements()}
    </section>
  );
}
